import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import API_BASE_URL from '../config/apiConfig';
import './Profile.css';

const Profile = ({ isVisible, onBack, onLogout, onNavClick }) => {
  const [activeTab, setActiveTab] = useState('account');
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    countryCode: 'US'
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Load user profile from API
  const loadUserProfile = async () => {
    setIsLoadingProfile(true);
    setProfileError('');
    
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        // Fallback to localStorage if no token
        const user = localStorage.getItem('user');
        if (user) {
          try {
            const userData = JSON.parse(user);
            setFormData({
              fullName: userData.name || '',
              phone: userData.phone || '',
              email: userData.email || '',
              countryCode: userData.countryCode || 'US'
            });
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
        setIsLoadingProfile(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (data.success && data.user) {
        setFormData({
          fullName: data.user.name || '',
          phone: data.user.phone || '',
          email: data.user.email || '',
          countryCode: data.user.countryCode || 'US'
        });
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        // Dispatch custom event to update sidebar
        window.dispatchEvent(new Event('userProfileUpdated'));
      } else {
        setProfileError(data.message || 'Failed to load profile');
        // Fallback to localStorage
        const user = localStorage.getItem('user');
        if (user) {
          try {
            const userData = JSON.parse(user);
            setFormData({
              fullName: userData.name || '',
              phone: userData.phone || '',
              email: userData.email || '',
              countryCode: userData.countryCode || 'US'
            });
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
      }
    } catch (error) {
      console.error('Load profile error:', error);
      setProfileError('Connection error. Please try again.');
      // Fallback to localStorage
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          setFormData({
            fullName: userData.name || '',
            phone: userData.phone || '',
            email: userData.email || '',
            countryCode: userData.countryCode || 'US'
          });
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Load user info when component mounts or becomes visible
  useEffect(() => {
    if (isVisible) {
      loadUserProfile();
    }
  }, [isVisible]);

  // Handle save profile
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        setProfileError('Please login to update profile');
        setIsSavingProfile(false);
        return;
      }

      // Extract phone number without country code
      const phoneNumber = formData.phone.replace(/^\+\d+\s*/, '').trim();

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.fullName,
          phone: phoneNumber,
          countryCode: formData.countryCode
        }),
      });

      const data = await response.json();

      if (data.success) {
        setProfileSuccess('Profile updated successfully!');
        // Update localStorage
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          // Dispatch custom event to update sidebar
          window.dispatchEvent(new Event('userProfileUpdated'));
        }
        // Clear success message after 3 seconds
        setTimeout(() => setProfileSuccess(''), 3000);
      } else {
        setProfileError(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      setProfileError('Connection error. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  if (!isVisible) return null;

  const tabs = [
    { id: 'account', label: 'Account information' },
    { id: 'password', label: 'Change Password' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'preferences', label: 'Preferences' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user types
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Password validation
  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Please enter your current password';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'Please enter a new password';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[0-9!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain at least one number or symbol';
    } else if (!/[a-z]/.test(passwordData.newPassword) || !/[A-Z]/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain both uppercase and lowercase letters';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordSuccess('');
    
    if (!validatePassword()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      const user = localStorage.getItem('user');
      let userEmail = formData.email;
      
      if (user) {
        try {
          const userData = JSON.parse(user);
          userEmail = userData.email || formData.email;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          email: userEmail,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setPasswordErrors({});
        // Clear success message after 3 seconds
        setTimeout(() => setPasswordSuccess(''), 3000);
      } else {
        setPasswordErrors({ 
          currentPassword: data.message || 'Failed to change password' 
        });
      }
    } catch (error) {
      console.error('Change password error:', error);
      setPasswordErrors({ 
        currentPassword: 'Connection error. Please try again.' 
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const countryCodes = [
    { code: 'DE', flag: '🇩🇪', dial: '+49' },
    { code: 'US', flag: '🇺🇸', dial: '+1' },
    { code: 'VN', flag: '🇻🇳', dial: '+84' },
    { code: 'FR', flag: '🇫🇷', dial: '+33' },
  ];

  const currentCountry = countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0];

  return (
    <div className="dashboard-container">
      <Sidebar activeId="profile" onLogout={onLogout} onNavClick={onNavClick} />

      <div className="main-content profile-content">
        <div className="profile-container">
          {/* Header */}
          <div className="profile-header">
            <h1 className="profile-title">Profile</h1>
          </div>

          {/* Tabs */}
          <div className="profile-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Nội dung Tab */}
          {activeTab === 'account' && (
            <div className="profile-main-area">
              {isLoadingProfile && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="loading-spinner">Loading profile...</div>
                </div>
              )}

              {!isLoadingProfile && (
                <>
                  {/* Error/Success Messages */}
                  {profileError && (
                    <div className="profile-error-message">
                      {profileError}
                    </div>
                  )}
                  {profileSuccess && (
                    <div className="profile-success-message">
                      {profileSuccess}
                    </div>
                  )}

                  {/* Avatar Section */}
                  <div className="profile-avatar-section">
                    <div className="avatar-wrapper">
                      <div className="profile-avatar-initial">
                        {formData.fullName ? formData.fullName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <button className="avatar-edit-btn">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Form Section */}
                  <div className="profile-form">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    className="form-input"
                    value={formData.fullName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <div className="phone-input-group">
                    <div className="country-select-wrapper">
                      <div className="country-display">
                        <span>{currentCountry.flag}</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                      </div>
                      <select 
                        name="countryCode" 
                        value={formData.countryCode} 
                        onChange={handleInputChange}
                      >
                        {countryCodes.map(c => (
                          <option key={c.code} value={c.code}>{c.dial} {c.code}</option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      name="phone"
                      className="form-input phone-input"
                      value={formData.phone ? `${currentCountry.dial} ${formData.phone}` : ''}
                      placeholder={`${currentCountry.dial} Enter phone number`}
                      onChange={(e) => {
                         // Logic đơn giản để user sửa số sau mã vùng
                         const val = e.target.value;
                         const dial = currentCountry.dial;
                         if(val.startsWith(dial)) {
                           setFormData(prev => ({...prev, phone: val.replace(dial, '').trim()}));
                         } else if (val === '') {
                           setFormData(prev => ({...prev, phone: ''}));
                         }
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    disabled
                    style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
                    Email cannot be changed
                  </small>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {/* Password Change Tab */}
          {activeTab === 'password' && (
            <div className="profile-main-area">
              <div className="profile-form">
                {passwordSuccess && (
                  <div className="password-success-message">
                    {passwordSuccess}
                  </div>
                )}

                <form onSubmit={handleChangePassword}>
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        name="currentPassword"
                        className={`form-input ${passwordErrors.currentPassword ? 'input-error' : ''}`}
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      >
                        {showPasswords.current ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <span className="error-message">{passwordErrors.currentPassword}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        name="newPassword"
                        className={`form-input ${passwordErrors.newPassword ? 'input-error' : ''}`}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      >
                        {showPasswords.new ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <span className="error-message">{passwordErrors.newPassword}</span>
                    )}
                    {passwordData.newPassword && !passwordErrors.newPassword && (
                      <div className="password-requirements">
                        <div className={`requirement ${passwordData.newPassword.length >= 8 ? 'valid' : ''}`}>
                          At least 8 characters
                        </div>
                        <div className={`requirement ${/[0-9!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? 'valid' : ''}`}>
                          Contains number or symbol
                        </div>
                        <div className={`requirement ${/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? 'valid' : ''}`}>
                          Contains uppercase and lowercase
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirmPassword"
                        className={`form-input ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      >
                        {showPasswords.confirm ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                    {passwordErrors.confirmPassword && (
                      <span className="error-message">{passwordErrors.confirmPassword}</span>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Placeholder cho các tab khác */}
          {activeTab !== 'account' && activeTab !== 'password' && (
            <div style={{textAlign: 'center', marginTop: '40px', color: '#9ca3af'}}>
              Content for {tabs.find(t => t.id === activeTab)?.label}
            </div>
          )}
        </div>

        {/* Footer Area - Luôn hiển thị ở đáy */}
        <div className="profile-footer">
          <span className="footer-hint">
            {activeTab === 'password' ? 'Change your password' : 'Edit your account information'}
          </span>
          {activeTab === 'password' ? (
            <button 
              className="save-btn" 
              onClick={handleChangePassword}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </button>
          ) : (
            <button 
              className="save-btn"
              onClick={handleSaveProfile}
              disabled={isSavingProfile || isLoadingProfile}
            >
              {isSavingProfile ? 'Saving...' : 'Save updates'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default Profile;